import { ErrorTypes, useDashboardStore } from "@/stores";
import { Lock, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function SecureLoader({ pendingMessage = "Securing your offer...", completeMessage = "Offer Secured!" }: { pendingMessage?: string; completeMessage?: string }) {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const setErrorState = useDashboardStore((s) => s.setErrorState);
  const setIsSecuringQuote = useDashboardStore((s) => s.setIsSecuringQuote);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setIsComplete(true);
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 60); // Updates every 60ms to reach 100% in 3 seconds

    // Timeout after 30 seconds to prevent users from getting stuck
    const timeout = setTimeout(() => {
      setIsSecuringQuote(false);

      const errorDetails = {
        message: "The secure quote request timed out after 30 seconds",
        code: "TIMEOUT",
        timestamp: new Date(),
        status: 408, // Request Timeout
      };

      setErrorState(ErrorTypes.SECURE_QUOTE_ERROR, errorDetails);
      // Navigate back to the previous page (view-offer)
      navigate(-1);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [setErrorState, setIsSecuringQuote, navigate]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-3xl bg-white/5">
      <div className="text-center flex flex-col gap-2">
        <div className="mx-auto mb-4 flex justify-center">
          <div className="relative">
            <div className="w-28 h-28 rounded-full bg-blue-500 flex items-center justify-center">
              <Shield className="w-12 h-12 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>
        {/* Loading circle below the shield */}
        <div className="mt-6 flex justify-center">
          <div className="relative w-16 h-16">
            <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="#e5e7eb"
                strokeWidth="6"
                fill="none"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="#3b82f6"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
                className="transition-all duration-300 ease-out"
              />
            </svg>
            {/* Three dots in the center */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex space-x-1">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
        <h2 className="text-2xl font-semibold mb-2">
          {isComplete ? completeMessage : pendingMessage}
        </h2>
        <p className="text-blue-500 font-semibold mb-4">
          {Math.round(progress)}% Complete
        </p>
        <div className="flex flex-row gap-2 items-center">
          <Lock className="w-4 h-4 text-green-500" />
          <p className="text-gray-500 flex items-center justify-center">
            Your data is encrypted and securely transmitted.
          </p>
        </div>
        <div className="flex justify-center space-x-4 text-sm text-gray-600 items-center mt-4">
          <div className="flex flex-row items-center gap-1">
            <div className={`w-2 h-2 rounded-full bg-green-500`}></div>
            <span className="text-gray-400">SSL Encrypted</span>
          </div>
          <div className="flex flex-row items-center gap-1">
            <div className={`w-2 h-2 rounded-full bg-blue-500`}></div>
            <span className="text-gray-400">Bank-Grade Security</span>
          </div>
        </div>
      </div>
    </div>
  );
}
